import { useNavigate } from "react-router-dom";
import useLieferanten from "@/hooks/useLieferanten";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScanBarcode, ShoppingCart, ChevronRight, ClipboardList, LayoutGrid } from "lucide-react";

/**
 * Ware bestellen – ein Knopf in der Kopfzeile, ein Menü je Lieferant.
 *
 * Warum in der Kopfzeile neben "Aufgabe anlegen": Nachbestellt wird, wenn
 * jemand im Labor die letzte Packung aus dem Regal nimmt. Dann steht er mit
 * dem Telefon in der Hand am Regal, nicht am Schreibtisch. Der Weg muss
 * deshalb von jeder Seite aus gleich kurz sein: Knopf, Lieferant, scannen.
 *
 * Die Liste der Lieferanten kommt vom Server (hooks/useLieferanten.js), damit
 * ein neuer Shop nur dort eingetragen werden muss - die Oberfläche zeigt ihn
 * dann von selbst. Auf dem Handy sitzt derselbe Weg in der Leiste unten.
 */
export default function WareBestellen() {
  const navigate = useNavigate();
  // Dieselbe Liste wie im Blatt "Bestellen" der Handy-Leiste (BestellenWahl).
  const lieferanten = useLieferanten();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title="Ware bestellen" data-testid="ware-bestellen-knopf"
                className="h-9 rounded-lg flex items-center gap-1.5 px-2 md:px-3
                           text-slate-600 hover:bg-slate-100 hover:text-slate-900 shrink-0
                           lg:border lg:border-slate-200 lg:bg-white lg:shadow-sm lg:hover:bg-slate-50 transition-colors">
          <ShoppingCart className="h-[18px] w-[18px]" />
          <span className="hidden lg:inline text-sm font-semibold">Bestellen</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs text-slate-500">Ware bestellen bei</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {lieferanten.map((l) => (
          <DropdownMenuItem key={l.id} disabled={!l.aktiv}
                            data-testid={`ware-bestellen-${l.id}`}
                            onSelect={() => navigate(`/bestellen/${l.id}`)}
                            className="py-2.5 cursor-pointer">
            {l.scan ? <ScanBarcode className="h-4 w-4 mr-2.5 text-slate-500 shrink-0" /> : <LayoutGrid className="h-4 w-4 mr-2.5 text-slate-500 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">{l.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{l.beschreibung}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/bestellungen")} data-testid="ware-bestellen-uebersicht" className="py-2 cursor-pointer">
          <ClipboardList className="h-4 w-4 mr-2.5 text-slate-500 shrink-0" />
          <div className="text-sm text-slate-900">Bestellübersicht &amp; Freigabe</div>
        </DropdownMenuItem>
        <div className="px-2 py-1.5 text-[11px] text-slate-400">
          Weitere Lieferanten folgen, sobald ihr Bestellweg steht.
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
